"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { LIVE_STATUSES } from "@/contants";
import { formatUnixToVNDate, formatHandicapLabel } from "@/utils/format";
import {
  formatCorrectScoresForGrid,
  splitCorrectScoresListToSections,
  getMatchDisplayTime,
} from "@/utils/matchInfo";

import FootballOtherBetsDropdown from "../FootballOtherBetsDropdown";
import ViuGoTipsAiModal from "../ViuGoTipsAiModal";

import {
  formatValue,
  makeLeagueShortName,
  pickStableLeagueBg,
} from "./footballOddsMatchRowUtils";
import FootballOddsMobileRow from "./FootballOddsMobileRow";
import FootballOddsDesktopRow from "./FootballOddsDesktopRow";

export default function FootballOddsMatchRow({
  league,
  match,
}) {
  const { getMatchOdds, getMatchInsight } = useMatches();
  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const [openOtherOdds, setOpenOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsInitialTab, setTipsInitialTab] = useState("ai");
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [mobileOddsSlideIndex, setMobileOddsSlideIndex] = useState(0);
  const mobileOddsCarouselRef = useRef(null);
  const companyIdOdds = useSelector((state) => state.bookmakerReducer);

  const liveScore = getScore();
  const liveOdds = getOdds();
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);
  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
    currentHomeCorner,
    currentAwayCorner,
    homeYellow,
    awayYellow,
    homeRed,
    awayRed,
  } = useMatchData(liveScore, match);
  const odds = useMatchOdds(match, filterOdds, companyIdOdds);

  const statusNum =
    liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const { date: formattedDate, gio: formattedGio } = formatUnixToVNDate(match?.matchTime);

  useEffect(() => {
    // Reset slide when match changes to avoid mismatched scroll position.
    setMobileOddsSlideIndex(0);
    const el = mobileOddsCarouselRef.current;
    if (el) el.scrollLeft = 0;
  }, [match?.id]);

  const handleOtherOddsClick = () => {
    setOpenOtherOdds((prev) => !prev);
    if (!openOtherOdds && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const openTipsWithTab = (tabId) => (e) => {
    e?.preventDefault?.();
    setTipsInitialTab(tabId);
    setOpenTipsAiModal(true);
  };

  const goalOddEvenOdds = otherOddsData?.goalOddEvenOdds ?? [];
  const correctScoresListFull = otherOddsData?.correctScoresOdds
    ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
    : [];
  const correctScoresHalf = splitCorrectScoresListToSections(
    otherOddsData?.correctScoresHalfOdds
      ? formatCorrectScoresForGrid(otherOddsData.correctScoresHalfOdds)
      : [],
  );
  const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds)
    ? otherOddsData.totalCornersOdds
    : [];
  const totalCornersHalfOdds = Array.isArray(otherOddsData?.totalCornersHalfOdds)
    ? otherOddsData.totalCornersHalfOdds
    : [];
  const goalOddEvenFirst = goalOddEvenOdds?.[0] ?? {};
  const hasOtherData =
    goalOddEvenOdds?.length > 0 ||
    correctScoresListFull.length > 0 ||
    totalCornersOdds.length > 0 ||
    totalCornersHalfOdds.length > 0;

  const otherCount = Number(match.countOtherOdds ?? 0);
  const leagueName =
    league?.name ||
    league?.leagueName ||
    league?.league_name ||
    match?.league?.name ||
    match?.leagueName ||
    match?.league?.leagueName ||
    match?.league_name ||
    "";

  const leagueShort =
    league?.shortName ||
    league?.short_name ||
    league?.short ||
    league?.code ||
    match?.league?.shortName ||
    match?.league?.short_name ||
    match?.league?.short ||
    match?.leagueShortName ||
    match?.leagueCode ||
    match?.league?.code ||
    makeLeagueShortName(leagueName) ||
    "--";

  const displayTimeMobile = getMatchDisplayTime(
    liveScore,
    statusNum,
    match?.matchTime,
    match?.halfStartTime,
    true,
  );

  const minuteText =
    isLive && (displayTimeMobile?.type === "minute" || displayTimeMobile?.type === "status")
      ? String(displayTimeMobile?.content ?? "")
      : String(displayTimeMobile?.content ?? "");

  const leagueBg = useMemo(() => {
    const explicit =
      league?.color ||
      league?.bgColor ||
      match?.league?.color ||
      match?.leagueColor;
    if (explicit) return explicit;
    return pickStableLeagueBg(league?.id ?? match?.league?.id ?? match?.leagueId ?? leagueShort);
  }, [
    league?.color,
    league?.bgColor,
    league?.id,
    match?.league?.color,
    match?.leagueColor,
    match?.league?.id,
    match?.leagueId,
    leagueShort,
  ]);

  const ft = {
    home: formatValue(odds.homeHandicapOdds),
    line: formatHandicapLabel(odds.handicapValue, true),
    away: formatValue(odds.awayHandicapOdds),
    over: formatValue(odds.overOdds),
    ouLine: formatValue(odds.overUnderValue),
    under: formatValue(odds.underOdds),
    homeTrend: odds.homeHandicapTrend,
    awayTrend: odds.awayHandicapTrend,
    overTrend: odds.overTrend,
    underTrend: odds.underTrend,
  };

  const ft1x2 = {
    home: formatValue(odds.homeEuropeOdds),
    draw: formatValue(odds.instantDrawOdds),
    away: formatValue(odds.awayEuropeOdds),
    homeTrend: odds.homeEuropeTrend,
    drawTrend: odds.drawTrend,
    awayTrend: odds.awayEuropeTrend,
  };

  const h1Handicap = {
    home: formatValue(odds.homeHandicapHalfOdds),
    line: formatHandicapLabel(odds.handicapHalfValue, true),
    away: formatValue(odds.awayHandicapHalfOdds),
    homeTrend: odds.homeHandicapHalfTrend,
    awayTrend: odds.awayHandicapHalfTrend,
  };

  const h1Ou = {
    over: formatValue(odds.overHalfOdds),
    ouLine: formatValue(odds.overUnderHalfValue),
    under: formatValue(odds.underHalfOdds),
    overTrend: odds.overHalfTrend,
    underTrend: odds.underHalfTrend,
  };

  const h11x2 = {
    home: formatValue(odds.homeEuropeHalfOdds),
    draw: formatValue(odds.drawHalfOdds),
    away: formatValue(odds.awayEuropeHalfOdds),
    homeTrend: odds.homeEuropeHalfTrend,
    drawTrend: odds.drawHalfTrend,
    awayTrend: odds.awayEuropeHalfTrend,
  };

  return (
    <>
      <FootballOddsMobileRow
        league={league}
        match={match}
        formattedGio={formattedGio}
        leagueShort={leagueShort}
        leagueBg={leagueBg}
        isLive={isLive}
        minuteText={minuteText}
        currentHomeScore={currentHomeScore}
        currentAwayScore={currentAwayScore}
        currentHomeHalfScore={currentHomeHalfScore}
        currentAwayHalfScore={currentAwayHalfScore}
        currentHomeCorner={currentHomeCorner}
        currentAwayCorner={currentAwayCorner}
        homeYellow={homeYellow}
        awayYellow={awayYellow}
        homeRed={homeRed}
        awayRed={awayRed}
        otherCount={otherCount}
        handleOtherOddsClick={handleOtherOddsClick}
        openTipsWithTab={openTipsWithTab}
        mobileOddsCarouselRef={mobileOddsCarouselRef}
        mobileOddsSlideIndex={mobileOddsSlideIndex}
        setMobileOddsSlideIndex={setMobileOddsSlideIndex}
        h1Handicap={h1Handicap}
        h1Ou={h1Ou}
        h11x2={h11x2}
        ft={ft}
        ft1x2={ft1x2}
      />

      <FootballOddsDesktopRow
        league={league}
        match={match}
        leagueBg={leagueBg}
        leagueShort={leagueShort}
        formattedGio={formattedGio}
        isLive={isLive}
        minuteText={minuteText}
        currentHomeScore={currentHomeScore}
        currentAwayScore={currentAwayScore}
        currentHomeCorner={currentHomeCorner}
        currentAwayCorner={currentAwayCorner}
        homeYellow={homeYellow}
        homeRed={homeRed}
        awayYellow={awayYellow}
        awayRed={awayRed}
        h1Handicap={h1Handicap}
        h1Ou={h1Ou}
        h11x2={h11x2}
        ft={ft}
        ft1x2={ft1x2}
        otherCount={otherCount}
        handleOtherOddsClick={handleOtherOddsClick}
        openTipsWithTab={openTipsWithTab}
      />

      {openOtherOdds && (
        <tr>
          <td colSpan={8} className="bg-[var(--theme-foreground)] p-0">
            <FootballOtherBetsDropdown
              loadingOtherOdds={loadingOtherOdds}
              otherOddsData={otherOddsData}
              hasOtherData={hasOtherData}
              isLive={isLive}
              goalOddEvenOdds={goalOddEvenOdds}
              goalOddEvenFirst={goalOddEvenFirst}
              correctScoresListFull={correctScoresListFull}
              correctScoresFull={correctScoresFull}
              correctScoresHalf={correctScoresHalf}
              totalCornersOdds={totalCornersOdds}
              totalCornersHalfOdds={totalCornersHalfOdds}
              odds={odds}
              fontBody="text-[12px]"
            />
          </td>
        </tr>
      )}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        initialActiveTab={tipsInitialTab}
        match={match}
        matchInfo={{
          formattedDate,
          formattedGio,
          isLive,
        }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow,
          homeRed,
          awayYellow,
          awayRed,
          currentHomeCorner,
          currentAwayCorner,
        }}
        oddsInfo={{
          handicapValue: odds.handicapValue,
          homeHandicapOdds: odds.homeHandicapOdds,
          awayHandicapOdds: odds.awayHandicapOdds,
          overUnderValue: odds.overUnderValue,
          overOdds: odds.overOdds,
          underOdds: odds.underOdds,
          homeEuropeOdds: odds.homeEuropeOdds,
          awayEuropeOdds: odds.awayEuropeOdds,
          instantDrawOdds: odds.instantDrawOdds,
          handicapHalfValue: odds.handicapHalfValue,
          homeHandicapHalfOdds: odds.homeHandicapHalfOdds,
          awayHandicapHalfOdds: odds.awayHandicapHalfOdds,
        }}
        leagueName={leagueName}
        leagueLogo={match?.league?.logo || match?.leagueLogo || ""}
        homeName={match.homeName}
        homeLogo={match?.homeTeam?.logo || match?.homeLogo || ""}
        awayName={match.awayName}
        awayLogo={match?.awayTeam?.logo || match?.awayLogo || ""}
        matchDate={formattedDate}
        matchTime={formattedGio}
        matchTimestamp={match?.matchTime}
        headerScore={`${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={isLive ? "Trực tiếp" : "Sắp diễn ra"}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </>
  );
}

